import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { UserPreferencesRequest } from '@Models';
import { bopsApi } from '@Network';
import { clearOnboardingSkipped, isOnboardingSkipped, markOnboardingSkipped } from '../utils/onboardingStorage';

export const useUserPreferences = (sourceName?: string) => {
  const queryClient = useQueryClient();

  const queryKey = ['user-preferences', sourceName];

  const { data: preferences, isLoading: isQueryLoading } = useQuery({
    queryKey,
    queryFn: () => bopsApi.getUserPreferences(sourceName),
    enabled: !!sourceName,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Keep the loading state truthy until an agent is selected. Prevents the
  // onboarding wizard from auto-opening based on a default response fetched
  // with sourceName=undefined during agent resolution.
  const isLoading = !sourceName || isQueryLoading;

  const saveAndCompleteMutation = useMutation({
    mutationFn: async (request: UserPreferencesRequest) => {
      await bopsApi.updateUserPreferences(request, sourceName);
      await bopsApi.completeOnboarding(sourceName);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: async (request: UserPreferencesRequest) => {
      await bopsApi.updateUserPreferences(request, sourceName);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deletePreferencesMutation = useMutation({
    mutationFn: async () => {
      await bopsApi.deleteUserPreferences(sourceName);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const isOnboardingComplete = preferences?.onboardingCompleted ?? false;
  const shouldShowOnboarding = !!sourceName && !isLoading && !isOnboardingComplete && !isOnboardingSkipped(sourceName);

  return {
    preferences,
    isLoading,
    isOnboardingComplete,
    shouldShowOnboarding,
    saveAndComplete: saveAndCompleteMutation.mutateAsync,
    isSaving: saveAndCompleteMutation.isPending,
    saveError: saveAndCompleteMutation.error,
    updatePreferences: updatePreferencesMutation.mutateAsync,
    isUpdating: updatePreferencesMutation.isPending,
    deletePreferences: deletePreferencesMutation.mutateAsync,
    isDeleting: deletePreferencesMutation.isPending,
    skipOnboarding: () => markOnboardingSkipped(sourceName),
    clearSkip: () => clearOnboardingSkipped(sourceName),
  };
};

export default useUserPreferences;
